import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../../utils/api';
import Bildvisare from '../../../components/common/Bildvisare';
import { arPdf } from '../../../utils/filtyper';
import { bilageRutaKlass, BilageInnehall } from '../utils/prislistaBilaga';

/**
 * IA-prislistan med full redigering: lista, skapa, ändra, ta bort och exempelbild.
 *
 * Låg tidigare inbakad i Dashboard. Inredningsarkitekterna behövde nå den var de
 * än befinner sig när en kund frågar vad något kostar, och en andra kopia av
 * samma CRUD hade garanterat drivit isär — det har hänt flera gånger i den här
 * kodbasen. Därför är den utbruten och används av både Dashboard-fliken och
 * PrislistaPanel, sidopanelen som navbar-knappen öppnar.
 *
 * Prislistan är KUNDVÄND: kunden ser samma poster i Ritningar och kan anmäla
 * intresse. En ändring här syns alltså för kunder, inte bara internt.
 *
 * `kompakt` byter tabellen mot en smal lista där varje post visar namn, bild,
 * beskrivning och pris, och fäller ut kategori, status och åtgärder vid klick.
 * Sidopanelen är för smal för femkolumnstabellen; Dashboard-fliken har plats
 * och behåller den.
 */
const PrislistaHantering = ({ visaRubrik = true, kompakt = false }) => {
  const [iaPriceItems, setIaPriceItems] = useState([]);
  const [iaPriceLoading, setIaPriceLoading] = useState(false);
  const [iaPriceForm, setIaPriceForm] = useState({ name: '', description: '', vadFarDu: '', price: '', priceText: '', priceUnit: 'kr/st', category: 'Ritningar', sortOrder: 0 });
  const [iaPriceImageFile, setIaPriceImageFile] = useState(null);
  const [iaPriceImagePreview, setIaPriceImagePreview] = useState(null);
  const [iaPriceImageUploading, setIaPriceImageUploading] = useState(false);
  const [editingPriceItem, setEditingPriceItem] = useState(null);
  const [showIaPriceForm, setShowIaPriceForm] = useState(false);
  const [visadBild, setVisadBild] = useState(null);
  const [utfalldPost, setUtfalldPost] = useState(null);

  // I kompakt läge finns inte plats för två kolumner i formuläret. col-span-2
  // måste bort samtidigt — en spann över två kolumner i ett engrids-rutnät
  // skapar en osynlig andra kolumn och trycker fälten ur led.
  const formGridKlass = kompakt ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4';
  const formHelBreddKlass = kompakt ? '' : 'md:col-span-2';

  const visaPris = (item) =>
    item.priceText || `${item.price?.toLocaleString('sv-SE')} ${item.priceUnit}`;

  // Exempelbilagan kan vara en PDF — ett prisexempel över flera sidor går inte
  // att få till som en bild. Fältet heter fortsatt exampleImage i modellen.
  //
  // En nyvald fil förhandsvisas från en blob-URL, som inte bär någon ändelse.
  // Därför avgörs den på file.type, och bara en sparad URL på ändelsen.
  const forhandsvisningArPdf = iaPriceImageFile
    ? iaPriceImageFile.type === 'application/pdf'
    : arPdf(editingPriceItem?.exampleImage);

  // Klick på raden visar exempelbilden stort — samma sak som kunden ser i
  // Ritningar. Redigering ligger på en egen knapp: att gå rakt in i formuläret
  // gjorde bilden omöjlig att bara titta på.
  const oppnaRedigering = (item) => {
    setEditingPriceItem(item);
    setIaPriceForm({
      name: item.name,
      description: item.description || '',
      vadFarDu: item.vadFarDu || '',
      price: item.price,
      priceText: item.priceText || '',
      priceUnit: item.priceUnit || 'kr/st',
      category: item.category || 'Övrigt',
      sortOrder: item.sortOrder || 0
    });
    setIaPriceImageFile(null);
    setIaPriceImagePreview(null);
    setShowIaPriceForm(true);
  };

  const fetchIaPriceItems = useCallback(async () => {
    setIaPriceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ia-prislista`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setIaPriceItems(await res.json());
    } catch (err) {
      console.error('Fel vid hämtning av IA-prislista:', err);
    } finally {
      setIaPriceLoading(false);
    }
  }, []);

  useEffect(() => { fetchIaPriceItems(); }, [fetchIaPriceItems]);

  const handleIaPriceSave = async () => {
    const token = localStorage.getItem('token');
    const method = editingPriceItem ? 'PATCH' : 'POST';
    const url = editingPriceItem
      ? `${API_URL}/api/ia-prislista/${editingPriceItem._id}`
      : `${API_URL}/api/ia-prislista`;

    try {
      const payload = { ...iaPriceForm, price: Number(iaPriceForm.price) || 0 };
      if (!payload.priceText) payload.priceText = null;

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const savedItem = await res.json();

        // Ladda upp bilagan om en är vald. Svaret kontrolleras: tidigare
        // ignorerades det helt, så en avvisad fil — för stor, eller en filtyp
        // servern inte tar — sparade posten utan bilaga och utan ett ord om
        // varför. Posten är redan sparad här, så felet avbryter inte resten.
        if (iaPriceImageFile) {
          setIaPriceImageUploading(true);
          const formData = new FormData();
          formData.append('image', iaPriceImageFile);
          const bilageSvar = await fetch(`${API_URL}/api/ia-prislista/${savedItem._id}/image`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          setIaPriceImageUploading(false);
          if (!bilageSvar.ok) {
            const fel = await bilageSvar.json().catch(() => ({}));
            alert(`Prisposten sparades, men bilagan kunde inte laddas upp.\n\n${fel.message || 'Okänt fel'}`);
          }
        }

        setShowIaPriceForm(false);
        setEditingPriceItem(null);
        setIaPriceForm({ name: '', description: '', vadFarDu: '', price: '', priceText: '', priceUnit: 'kr/st', category: 'Ritningar', sortOrder: 0 });
        setIaPriceImageFile(null);
        setIaPriceImagePreview(null);
        fetchIaPriceItems();
      } else {
        const fel = await res.json().catch(() => ({}));
        alert(fel.message || 'Kunde inte spara prisposten');
      }
    } catch (err) {
      console.error('Fel vid sparande av IA-prispost:', err);
      setIaPriceImageUploading(false);
    }
  };

  const handleIaPriceDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Vill du ta bort denna prispost?')) return;
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/ia-prislista/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchIaPriceItems();
    } catch (err) {
      console.error('Fel vid borttagning:', err);
    }
  };

  return (
    <div className="space-y-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          {visaRubrik && <h2 className="text-lg font-semibold text-gray-900">IA-prislista</h2>}
          <button
            onClick={() => {
              setEditingPriceItem(null);
              setIaPriceForm({ name: '', description: '', vadFarDu: '', price: '', priceUnit: 'kr/st', category: 'Ritningar', sortOrder: 0 });
              setShowIaPriceForm(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md active:scale-95"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Lägg till tjänst
          </button>
        </div>

        {showIaPriceForm && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-medium mb-4">{editingPriceItem ? 'Redigera tjänst' : 'Ny tjänst'}</h3>
            <div className={formGridKlass}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Namn</label>
                <input
                  type="text"
                  value={iaPriceForm.name}
                  onChange={(e) => setIaPriceForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  placeholder="T.ex. Färgkonsultation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                <select
                  value={iaPriceForm.category}
                  onChange={(e) => setIaPriceForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                >
                  <option>Ritningar</option>
                  <option>Designkoncept</option>
                  <option>Rådgivning</option>
                  <option>Platsbyggnation</option>
                  <option>Belysning</option>
                  <option>Färgsättning</option>
                  <option>Konsultation</option>
                  <option>Design</option>
                  <option>Övrigt</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pris</label>
                <input
                  type="number"
                  value={iaPriceForm.price}
                  onChange={(e) => setIaPriceForm(prev => ({ ...prev, price: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prisenhet</label>
                <input
                  type="text"
                  value={iaPriceForm.priceUnit}
                  onChange={(e) => setIaPriceForm(prev => ({ ...prev, priceUnit: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  placeholder="kr/st"
                />
              </div>
              <div className={formHelBreddKlass}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prisspann (valfritt)</label>
                <input
                  type="text"
                  value={iaPriceForm.priceText}
                  onChange={(e) => setIaPriceForm(prev => ({ ...prev, priceText: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  placeholder="T.ex. 11 000 – 24 500 kr (visas istället för pris om ifyllt)"
                />
              </div>
              <div className={formHelBreddKlass}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivning</label>
                <textarea
                  value={iaPriceForm.description}
                  onChange={(e) => setIaPriceForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  placeholder="Beskrivning av tjänsten..."
                />
              </div>
              <div className={formHelBreddKlass}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vad får du? <span className="font-normal text-gray-500">(internt — läses in som beskrivning när IA skapar ett tillval)</span>
                </label>
                <textarea
                  value={iaPriceForm.vadFarDu}
                  onChange={(e) => setIaPriceForm(prev => ({ ...prev, vadFarDu: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  placeholder={'- 1 timmes konsultation på kontoret eller digitalt\n- 3D-vyer – färg- och materialsatt\n- Förslag på inredning\n- 1 revidering'}
                />
              </div>
              <div className={formHelBreddKlass}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exempelbild eller PDF (valfritt)
                </label>
                {(iaPriceImagePreview || (editingPriceItem?.exampleImage && !iaPriceImageFile)) && (
                  <div className="relative inline-block mb-2">
                    {forhandsvisningArPdf ? (
                      /* Liggande A4, inte kvadrat: en kvadratisk ruta visade
                         bara en remsa av sidan. Samma form som miniatyrerna,
                         se utils/prislistaBilaga. */
                      <embed
                        src={`${iaPriceImagePreview || editingPriceItem?.exampleImage}#toolbar=0&navpanes=0&scrollbar=0&view=Fit&page=1`}
                        type="application/pdf"
                        className="w-56 aspect-[297/210] rounded-lg border border-gray-200 bg-white"
                      />
                    ) : (
                      <img
                        src={iaPriceImagePreview || editingPriceItem?.exampleImage}
                        alt="Förhandsgranskning"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                      />
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        if (editingPriceItem?.exampleImage && !iaPriceImageFile) {
                          const token = localStorage.getItem('token');
                          await fetch(`${API_URL}/api/ia-prislista/${editingPriceItem._id}/image`, {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          setEditingPriceItem(prev => ({ ...prev, exampleImage: null }));
                          fetchIaPriceItems();
                        }
                        setIaPriceImageFile(null);
                        setIaPriceImagePreview(null);
                      }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setIaPriceImageFile(file);
                      setIaPriceImagePreview(URL.createObjectURL(file));
                    }
                  }}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-pink-50 file:text-leadme-pink hover:file:bg-pink-100"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { setShowIaPriceForm(false); setEditingPriceItem(null); setIaPriceImageFile(null); setIaPriceImagePreview(null); }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg"
              >
                Avbryt
              </button>
              <button
                onClick={handleIaPriceSave}
                disabled={!iaPriceForm.name || (!iaPriceForm.price && !iaPriceForm.priceText) || iaPriceImageUploading}
                className="px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark text-white text-sm font-medium rounded-lg disabled:opacity-50"
              >
                {iaPriceImageUploading ? 'Laddar upp...' : editingPriceItem ? 'Spara' : 'Lägg till'}
              </button>
            </div>
          </div>
        )}

        {iaPriceLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-leadme-pink"></div>
          </div>
        ) : iaPriceItems.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Inga tjänster tillagda ännu.</div>
        ) : kompakt ? (
          /* Smal lista: namn, bild, beskrivning och pris syns direkt, resten
             fälls ut. Femkolumnstabellen nedan kräver 600 px och får inte plats
             i sidopanelen. */
          <ul className="bg-white rounded-xl shadow-lg divide-y divide-gray-200 overflow-hidden">
            {iaPriceItems.map(item => {
              const utfalld = utfalldPost === item._id;
              return (
                <li key={item._id}>
                  <div
                    className="px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => setUtfalldPost(utfalld ? null : item._id)}
                  >
                    {item.exampleImage && (
                      /* Bilden öppnas stort direkt — den är ofta hela svaret på
                         kundens fråga, och ska inte kräva en utfällning först. */
                      <div
                        className={`relative flex-shrink-0 rounded overflow-hidden cursor-zoom-in group/img ${bilageRutaKlass(item.exampleImage, { bredd: 'w-12', breddPdf: 'w-24', bildHojd: 'h-12' })}`}
                        onClick={(e) => { e.stopPropagation(); setVisadBild({ src: item.exampleImage, alt: item.name }); }}
                      >
                        <BilageInnehall src={item.exampleImage} alt={item.name} />
                        <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/25 transition-colors flex items-center justify-center">
                          <svg className="w-4 h-4 text-white opacity-0 group-hover/img:opacity-100 transition-opacity drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </div>
                    )}
                    {/* Priset ligger på egen rad, inte i namnraden. Det låg
                        tidigare i ett flex-shrink-0-block med whitespace-nowrap
                        längst till höger — och prissträngarna är 20–30 tecken
                        ("13 200 – 23 500 kr inkl. moms"). I sidopanelen, som är
                        384 px bred, tog priset och chevronen ihop ~200 px av
                        320 px innehåll. Med bilden och mellanrummen blev det
                        drygt 40 px kvar till namn och beskrivning, som därmed
                        bröts mitt i orden till en smal remsa. */}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm break-words">{item.name}</p>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">{visaPris(item)}</p>
                      {item.description && (
                        <p className="text-xs text-gray-500 break-words mt-1">{item.description}</p>
                      )}
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform ${utfalld ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {utfalld && (
                    <div className="px-4 py-2.5 flex items-center gap-3 bg-gray-50 border-t border-gray-100">
                      <span className="text-xs text-gray-600">{item.category}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {item.active !== false ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      <div className="ml-auto flex gap-1">
                        <button
                          onClick={() => oppnaRedigering(item)}
                          className="p-1.5 text-gray-400 hover:text-leadme-pink rounded-full transition-colors"
                          title={`Redigera ${item.name}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleIaPriceDelete(item._id)}
                          className="p-1.5 text-red-400 hover:text-red-600 rounded-full transition-colors"
                          title={`Ta bort ${item.name}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="min-w-[600px]">
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr_110px] gap-4 px-6 py-3 border-b border-gray-200 bg-gray-50">
                <div className="text-sm font-medium text-gray-500">Tjänst</div>
                <div className="text-sm font-medium text-gray-500">Kategori</div>
                <div className="text-sm font-medium text-gray-500">Pris</div>
                <div className="text-sm font-medium text-gray-500">Status</div>
                <div className="text-sm font-medium text-gray-500 text-center">Åtgärder</div>
              </div>
              <ul className="divide-y divide-gray-200">
                {iaPriceItems.map(item => (
                  <li
                    key={item._id}
                    className={`hover:bg-gray-50 ${item.exampleImage ? 'cursor-zoom-in' : ''}`}
                    onClick={() => {
                      if (item.exampleImage) setVisadBild({ src: item.exampleImage, alt: item.name });
                    }}
                  >
                    <div className="grid grid-cols-[2fr_1fr_1fr_1fr_110px] gap-4 px-6 py-4 items-start">
                      {/* min-w-0 på båda: grid-celler har min-width:auto som
                          standard, så en lång beskrivning vidgade kolumnen och
                          tryckte kategori, pris och status ur led. */}
                      <div className="flex items-start gap-3 min-w-0">
                        {item.exampleImage && (
                          <div className={`relative flex-shrink-0 rounded overflow-hidden group/img ${bilageRutaKlass(item.exampleImage, { bredd: 'w-14', breddPdf: 'w-28', bildHojd: 'h-14' })}`}>
                            <BilageInnehall src={item.exampleImage} alt={item.name} />
                            {/* Förstoringsglas vid hover — annars syns det inte att
                                bilden går att öppna stort. */}
                            <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/25 transition-colors flex items-center justify-center">
                              <svg className="w-5 h-5 text-white opacity-0 group-hover/img:opacity-100 transition-opacity drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                              </svg>
                            </div>
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          {item.description && <p className="text-sm text-gray-500 break-words">{item.description}</p>}
                        </div>
                      </div>
                      <div className="text-gray-600">{item.category}</div>
                      <div className="text-gray-900 font-medium">{visaPris(item)}</div>
                      <div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {item.active !== false ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                      <div className="flex justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => oppnaRedigering(item)}
                          className="p-2 text-gray-400 hover:text-leadme-pink rounded-full transition-colors"
                          title={`Redigera ${item.name}`}
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleIaPriceDelete(item._id)}
                          className="p-2 text-red-400 hover:text-red-600 rounded-full transition-colors"
                          title={`Ta bort ${item.name}`}
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Helskärmsvisning av exempelbild — samma komponent som kundvyn använder */}
        {visadBild && (
          <Bildvisare
            src={visadBild.src}
            alt={visadBild.alt}
            onClose={() => setVisadBild(null)}
          />
        )}
    </div>
  );
};

export default PrislistaHantering;
